// Screen Atlas (E01-S3-T1, E01-S3-T2; contracts 1.0 section 2): what the assistant may recognise
// and read in one application, for one environment. Deny by default — a field that is not here is
// not read — so every rule below is about keeping what IS here narrow: exact origins, bounded
// regular expressions (they run in the user's browser on every frame), classes that decide what
// leaves the device, and ids unique within the scope a reference resolves in.
import { z } from "zod";
import { checkPackPattern } from "./backstop/analyse.js";
import { BUILTIN_PATTERNS, CODE_OWNED_PATTERNS } from "./backstop/patterns.js";
import { ExprField } from "./expr/schema.js";
import { bandLabels, EntityKind } from "./frame/values.js";
import { ContextKey, FieldClass, Id, NonEmpty, PerceptionTier, RefId, strictObject, version, } from "./primitives.js";
// ── shared grammars ─────────────────────────────────────────────────────────
export const ATLAS_REGEX_MAX = 200;
/**
 * A regular expression written in an atlas. It must compile with the case-insensitive flag — the
 * only flag an atlas regex takes (D-99) — and be at most 200 characters, because it runs in the
 * page on every frame.
 */
export const AtlasRegex = z
    .string()
    .min(1)
    .max(ATLAS_REGEX_MAX, `a regex is at most ${String(ATLAS_REGEX_MAX)} characters`)
    .superRefine((s, ctx) => {
    try {
        new RegExp(s, "i");
    }
    catch (e) {
        ctx.addIssue({
            code: "custom",
            message: `regex does not compile with the i flag: ${e.message}`,
        });
    }
});
/** `{ regex }`, the object form an atlas uses wherever text may be matched by pattern. */
export const RegexMatch = strictObject({ regex: AtlasRegex });
const LABEL = "[a-z0-9](?:[a-z0-9-]*[a-z0-9])?";
/** scheme http/https, lower-case host (or a bracketed IPv6 literal), optional port; nothing else. */
export const ORIGIN_RE = new RegExp(`^https?://(?:${LABEL}(?:\\.${LABEL})*|\\[[0-9a-f:.]+\\])(?::\\d{1,5})?$`);
/**
 * An exact origin. No path (not even a trailing slash), no query, no wildcard, no user info, and
 * written as the browser writes it: a default port spelled out (https://x:443) is not exact.
 */
export const Origin = z
    .string()
    .regex(ORIGIN_RE, "expected an exact origin: http(s)://host[:port], lower case, no path or wildcard")
    .refine((s) => {
    try {
        return new URL(s).origin === s;
    }
    catch {
        return false;
    }
}, "not an origin as a browser writes it");
// Field, collection and context ids use the reference grammar (RefId, ContextKey in primitives.ts).
// ── locators ────────────────────────────────────────────────────────────────
// ADR-026: `within` is a region target id, as Signature.within (D-104): the search is scoped to that
// target's subtree, for an app that shows the same role and name twice. It names a target of the
// same screen or overlay (checked by scopeUnique below). Role and label locators only.
export const Locator = z.discriminatedUnion("by", [
    strictObject({
        by: z.literal("role"),
        role: NonEmpty,
        name: z.union([NonEmpty, RegexMatch]).optional(),
        // ADR-026: the element also has aria-selected="true" (a tab, an option, a grid row). Only the
        // literal true: a locator never asks for an unselected element.
        selected: z.literal(true).optional(),
        within: Id.optional(),
    }),
    strictObject({ by: z.literal("label"), text: NonEmpty, within: Id.optional() }),
    strictObject({
        by: z.literal("table"),
        header: NonEmpty,
        row: z.union([z.enum(["focused", "selected"]), z.number().int().nonnegative()]),
    }),
    strictObject({ by: z.literal("css"), selector: NonEmpty }),
]);
/**
 * A locator chain: tried in order, never empty. A role locator names its element; only a
 * collection's `rows` chain may omit the name, because a row has none (contracts 1.0 section 2).
 */
export const LocatorChain = z
    .array(Locator)
    .min(1, "a locator chain is never empty")
    .superRefine((chain, ctx) => {
    chain.forEach((l, i) => {
        if (l.by === "role" && l.name === undefined)
            ctx.addIssue({
                code: "custom",
                message: "a role locator names its element; only collection rows may omit the name",
                path: [i, "name"],
            });
    });
});
/** The `rows` and `selected` chains of a collection locate rows, which have no name: a role locator may leave it out. */
export const RowLocatorChain = z.array(Locator).min(1, "a locator chain is never empty");
// ── fields ──────────────────────────────────────────────────────────────────
export const FieldFormat = z.union([z.enum(["date", "number", "code"]), RegexMatch]);
export const AtlasField = strictObject({
    id: RefId,
    label: NonEmpty,
    cls: FieldClass,
    minTier: PerceptionTier,
    locator: LocatorChain,
    entity: EntityKind.optional(),
    enumValues: z.array(NonEmpty).min(1).optional(),
    format: FieldFormat.optional(),
    maxLength: z.number().int().positive().optional(),
    bands: z.array(z.number()).min(1).optional(),
    optional: z.boolean().optional(),
    guideRef: NonEmpty,
}).superRefine((f, ctx) => {
    const bad = (path, message) => {
        ctx.addIssue({ code: "custom", message: `field ${f.id}: ${message}`, path: [path] });
    };
    if (f.minTier < 1)
        bad("minTier", "minTier is 1 to 3; tier 0 reads no fields");
    if (f.cls === "identifier" && f.minTier < 2)
        bad("minTier", "an identifier is read at tier 2 or above");
    if (f.entity !== undefined && f.cls !== "identifier")
        bad("entity", "entity is only for identifier fields");
    if (f.enumValues !== undefined) {
        if (f.cls !== "enum")
            bad("enumValues", "enumValues is only for cls enum");
        if (new Set(f.enumValues).size !== f.enumValues.length)
            bad("enumValues", "enumValues are unique");
    }
    if (f.bands !== undefined) {
        if (f.cls !== "numeric")
            bad("bands", "bands is only for cls numeric");
        try {
            bandLabels(f.bands);
        }
        catch (e) {
            bad("bands", `bands must be strictly increasing thresholds of at least 1000 (${e.message})`);
        }
    }
    if (f.cls === "structural" && f.format === undefined)
        bad("format", "a structural field declares its format (D-100)");
    if (f.format !== undefined && f.cls !== "structural")
        bad("format", "format is only for cls structural (D-100)");
    if (f.maxLength !== undefined && f.cls !== "structural")
        bad("maxLength", "maxLength is only for cls structural (D-100)");
});
// ── collections, targets, actions, signatures ──────────────────────────────
/** The pager regex reads "Page 1 of 3": it must have the named groups `page` and `pages` (D-99). */
export const PagerRegex = AtlasRegex.refine((s) => s.includes("(?<page>") && s.includes("(?<pages>"), "a pager regex has the named groups page and pages");
export const AtlasCollection = strictObject({
    id: RefId,
    rows: RowLocatorChain,
    selected: RowLocatorChain,
    pageSize: LocatorChain.optional(),
    total: LocatorChain.optional(),
    pager: strictObject({
        locator: LocatorChain,
        parse: strictObject({ regex: PagerRegex }),
    }).optional(),
});
export const AtlasTarget = strictObject({
    id: Id,
    locator: LocatorChain,
    label: NonEmpty.optional(),
});
export const ActionConsequence = z.enum(["none", "internal", "external-irreversible"]);
export const AtlasAction = strictObject({
    id: Id,
    locator: LocatorChain,
    consequence: ActionConsequence,
});
/** `within` is a region target id. A signature with neither role nor text would match anything. */
export const Signature = strictObject({
    role: NonEmpty.optional(),
    text: z.union([NonEmpty, RegexMatch]).optional(),
    within: Id.optional(),
}).refine((s) => s.role !== undefined || s.text !== undefined, "a signature names a role or a text");
// ── signals (D-10, D-92) ────────────────────────────────────────────────────
export const SignalDetector = z.discriminatedUnion("kind", [
    strictObject({ kind: z.literal("first-view"), scope: z.literal("session") }),
    strictObject({
        kind: z.literal("dwell-visible"),
        target: Id,
        ratio: z.number().gt(0).max(1),
        ms: z.number().int().positive(),
        sticky: z.enum(["entity", "screen"]),
    }),
    strictObject({
        kind: z.literal("clicked"),
        target: Id,
        sticky: z.enum(["overlay", "screen"]),
        negate: z.boolean().optional(),
    }),
    strictObject({
        kind: z.literal("changed"),
        field: RefId,
        to: NonEmpty.optional(),
        scope: z.literal("entity"),
    }),
]);
/**
 * Derived (`when`: a pure expression over the current frame) or observed (`detector`: one of four
 * built-in detectors) — exactly one, as a union, so a consumer narrows on `"when" in signal`. The
 * preprocess guard names the mistake when both or neither are given.
 */
const SIGNAL_MESSAGE = "a signal has exactly one of when (derived) or detector (observed)";
const AtlasSignalShape = z.union([
    strictObject({ id: Id, when: ExprField }),
    strictObject({ id: Id, detector: SignalDetector }),
]);
export const AtlasSignal = z.preprocess((v, ctx) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
        const has = (k) => Object.prototype.hasOwnProperty.call(v, k);
        if (has("when") === has("detector")) {
            ctx.addIssue({ code: "custom", message: SIGNAL_MESSAGE });
            return z.NEVER;
        }
    }
    return v;
}, AtlasSignalShape);
// ── screens and overlays ────────────────────────────────────────────────────
/** A URLPattern pathname pattern, relative to the atlas's origins; never a query string (D-90). */
export const UrlPathPattern = z
    .string()
    .min(1)
    .max(ATLAS_REGEX_MAX)
    .regex(/^\/[^\s?#]*$/, "a url pattern is a pathname starting with / and carries no query or fragment");
/** A fragment route, matched on literal route segments (D-90). */
export const HashPattern = strictObject({
    hash: z
        .string()
        .min(1)
        .max(ATLAS_REGEX_MAX)
        .regex(/^#[^\s?]*$/, "a hash pattern starts with #"),
});
/** A navigable destination (D-30): a fixed address inside the origins — no wildcard, no query, no client data. */
export const EntryUrl = z
    .string()
    .min(1)
    .max(ATLAS_REGEX_MAX)
    .regex(/^[/#][^\s?*]*$/, "an entry url is a fixed path or fragment: no wildcard, no query");
export const ScreenMatch = strictObject({
    url: z
        .array(z.union([UrlPathPattern, HashPattern]))
        .min(1)
        .optional(),
    signature: z.array(Signature).min(1).optional(),
    headings: z.array(NonEmpty).min(1).optional(),
}).refine((m) => m.url !== undefined || m.signature !== undefined || m.headings !== undefined, "a screen matches by url, signature or headings");
/**
 * Screen context (D-48; T2's inferred definition): read from the text of a locator and constrained
 * to an enum. A value outside enumValues never reaches a frame, so screen.context stays enum-only.
 */
export const ContextSource = strictObject({
    locator: LocatorChain,
    enumValues: z.array(NonEmpty).min(1, "a context source lists its enum values"),
});
/** Every locator chain a scope declares, with its path inside the scope. */
function scopeChains(s) {
    const out = [];
    s.fields.forEach((f, i) => out.push({ chain: f.locator, path: ["fields", i, "locator"] }));
    s.targets.forEach((t, i) => out.push({ chain: t.locator, path: ["targets", i, "locator"] }));
    (s.actions ?? []).forEach((a, i) => out.push({ chain: a.locator, path: ["actions", i, "locator"] }));
    (s.collections ?? []).forEach((c, i) => {
        for (const key of ["rows", "selected", "pageSize", "total"]) {
            const chain = c[key];
            if (chain)
                out.push({ chain, path: ["collections", i, key] });
        }
        if (c.pager)
            out.push({ chain: c.pager.locator, path: ["collections", i, "pager", "locator"] });
    });
    for (const [key, src] of Object.entries(s.context ?? {}))
        out.push({ chain: src.locator, path: ["context", key, "locator"] });
    return out;
}
/** Report every repeated id in a list, at the index of its second appearance. */
export function addDuplicateIssues(items, path, what, ctx, key = "id") {
    const seen = new Set();
    (items ?? []).forEach((item, i) => {
        if (seen.has(item.id))
            ctx.addIssue({
                code: "custom",
                message: `duplicate ${what} id ${item.id}`,
                path: [...path, i, key],
            });
        seen.add(item.id);
    });
}
function scopeUnique(s, ctx, where) {
    addDuplicateIssues(s.fields, ["fields"], `field in ${where} ${s.id}:`, ctx);
    addDuplicateIssues(s.targets, ["targets"], `target in ${where} ${s.id}:`, ctx);
    addDuplicateIssues(s.actions, ["actions"], `action in ${where} ${s.id}:`, ctx);
    addDuplicateIssues(s.signals, ["signals"], `signal in ${where} ${s.id}:`, ctx);
    addDuplicateIssues(s.collections, ["collections"], `collection in ${where} ${s.id}:`, ctx);
    // References inside the scope name things the scope declares: a detector's target or field, a
    // signature's region. Deny by default: an id nothing declares is an authoring mistake.
    const fields = new Set(s.fields.map((f) => f.id));
    const targets = new Set(s.targets.map((t) => t.id));
    const actions = new Set((s.actions ?? []).map((a) => a.id));
    const pointable = (id) => targets.has(id) || actions.has(id) || fields.has(id);
    (s.signals ?? []).forEach((sig, i) => {
        if (!("detector" in sig))
            return;
        const d = sig.detector;
        if ((d.kind === "dwell-visible" || d.kind === "clicked") && !pointable(d.target))
            ctx.addIssue({
                code: "custom",
                message: `signal ${sig.id}: ${d.kind} target ${d.target} is not a target, action or field of ${where} ${s.id}`,
                path: ["signals", i, "detector", "target"],
            });
        if (d.kind === "changed" && !fields.has(d.field))
            ctx.addIssue({
                code: "custom",
                message: `signal ${sig.id}: changed field ${d.field} is not a field of ${where} ${s.id}`,
                path: ["signals", i, "detector", "field"],
            });
    });
    const signatures = Array.isArray(s.match)
        ? s.match
        : (s.match?.signature ?? []);
    signatures.forEach((sg, i) => {
        if (sg.within !== undefined && !targets.has(sg.within))
            ctx.addIssue({
                code: "custom",
                message: `signature within ${sg.within} is not a target of ${where} ${s.id}`,
                path: ["match", i, "within"],
            });
    });
    // A locator's region (ADR-026) is a target of the same scope, as a signature's is.
    for (const { chain, path } of scopeChains(s))
        chain.forEach((l, j) => {
            if ((l.by === "role" || l.by === "label") && l.within !== undefined && !targets.has(l.within))
                ctx.addIssue({
                    code: "custom",
                    message: `locator within ${l.within} is not a target of ${where} ${s.id}`,
                    path: [...path, j, "within"],
                });
        });
}
export const AtlasScreen = strictObject({
    id: Id,
    title: NonEmpty,
    match: ScreenMatch,
    entry: strictObject({ url: EntryUrl }).optional(),
    context: z.record(ContextKey, ContextSource).optional(),
    fields: z.array(AtlasField),
    collections: z.array(AtlasCollection).optional(),
    targets: z.array(AtlasTarget),
    actions: z.array(AtlasAction).optional(),
    signals: z.array(AtlasSignal).optional(),
    guideRef: NonEmpty.optional(),
}).superRefine((s, ctx) => {
    scopeUnique(s, ctx, "screen");
});
export const AtlasOverlay = strictObject({
    id: Id,
    match: z.array(Signature).min(1, "an overlay is matched by at least one signature"),
    fields: z.array(AtlasField),
    targets: z.array(AtlasTarget),
    actions: z.array(AtlasAction).optional(),
    signals: z.array(AtlasSignal).optional(),
}).superRefine((o, ctx) => {
    scopeUnique(o, ctx, "overlay");
});
/**
 * The ids a pack may not use, so a hit's id always means one check: the trusted patterns' and the
 * name check's (NAME_LIKE_ID in src/backstop, written here to keep atlas.ts off that module).
 */
const RESERVED_IDS = new Set([
    ...[...BUILTIN_PATTERNS, ...CODE_OWNED_PATTERNS].map((p) => p.id),
    "name-like",
]);
/**
 * A backstop pattern the atlas adds (src/backstop compiles them). It must be written in the pack
 * subset (checkPackPattern, D-100: no lookaround, backreference, nesting or unbounded quantifier, a
 * step bound from its own size, a non-empty shortest match) under an id of its own, so a pack that would be refused at load is refused at build.
 */
export const AtlasBackstopPattern = strictObject({ id: Id, regex: AtlasRegex }).superRefine((p, ctx) => {
    if (RESERVED_IDS.has(p.id))
        ctx.addIssue({
            code: "custom",
            path: ["id"],
            message: `backstop pattern id ${p.id} is a built-in or code-owned pattern's (D-100)`,
        });
    const verdict = checkPackPattern(p.regex);
    if (!verdict.ok)
        ctx.addIssue({
            code: "custom",
            path: ["regex"],
            message: `backstop pattern ${p.id} is outside the pack subset: ${verdict.rule} (D-100)`,
        });
});
export const ScreenAtlas = strictObject({
    v: version(1),
    app: strictObject({ id: Id, name: NonEmpty }),
    variant: Id,
    origins: z.array(Origin).min(1),
    screens: z.array(AtlasScreen).min(1),
    overlays: z.array(AtlasOverlay),
    backstop: strictObject({ patterns: z.array(AtlasBackstopPattern) }),
    unsupported: z.array(Id).optional(),
}).superRefine((a, ctx) => {
    addDuplicateIssues(a.screens, ["screens"], "screen", ctx);
    addDuplicateIssues(a.overlays, ["overlays"], "overlay", ctx);
    addDuplicateIssues(a.backstop.patterns, ["backstop", "patterns"], "backstop pattern", ctx);
    // One field id is one field: every declaration across screens and overlays agrees on what
    // leaves the device (rule 3). Otherwise a pii field on one screen could be structural on another.
    const shape = (f) => JSON.stringify([
        f.cls,
        f.minTier,
        f.entity ?? null,
        f.enumValues ?? null,
        f.bands ?? null,
        f.format ?? null,
        f.maxLength ?? null,
    ]);
    const first = new Map();
    const scopes = [
        { kind: "screens", items: a.screens },
        { kind: "overlays", items: a.overlays },
    ];
    for (const { kind, items } of scopes) {
        items.forEach((sc, i) => {
            sc.fields.forEach((f, j) => {
                const here = { shape: shape(f), where: `${kind.slice(0, -1)} ${sc.id}` };
                const prev = first.get(f.id);
                if (!prev) {
                    first.set(f.id, here);
                    return;
                }
                if (prev.shape !== here.shape)
                    ctx.addIssue({
                        code: "custom",
                        message: `field ${f.id} is declared differently on ${here.where} and ${prev.where}: one id, one class, tier, entity, enumValues, bands and format`,
                        path: [kind, i, "fields", j],
                    });
            });
        });
    }
    const seen = new Set();
    a.origins.forEach((o, i) => {
        if (seen.has(o))
            ctx.addIssue({ code: "custom", message: `duplicate origin ${o}`, path: ["origins", i] });
        seen.add(o);
    });
});
//# sourceMappingURL=atlas.js.map